"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useNoteNamesOnScore } from "@/hooks/use-notation";
import { useStudioNavigation } from "@/hooks/use-studio-navigation";
import { useStudioTick } from "@/hooks/use-studio-tick";
import type { Frieze } from "@/lib/harmony/frieze";
import { ScoreEngine } from "@/components/learn-piece/harmony/score-engine";
import { HEADER_HEIGHT_PX, RAIL_GAP_PX, layoutScore } from "@/components/learn-piece/harmony/score-layout";
import { FONT_MONO, TEXT_MUTE } from "@/components/ui/organisms/chord-practice/palette";

/**
 * Ce que les deux mains jouent, quand elles le jouent, et où l'on en est.
 *
 * ## C'est une partition, et c'est présenté comme telle
 *
 * L'objet portait déjà toutes les informations d'une partition : le temps en
 * abscisse et proportionnel, les barres de mesure, les attaques et les durées,
 * les deux mains séparées, les accords chiffrés, le nom des touches. Il ne lui
 * manquait que l'axe vertical, qu'il dépensait en **relatif** : deux bandes où
 * le trait montait et descendait dans l'ambitus de sa fenêtre. Cet axe-là ne se
 * comparait pas d'une fenêtre à l'autre et ne disait rien de l'octave.
 *
 * Deux portées le dépensent en **absolu**. Une note est à la place que son nom
 * lui donne, la même du début à la fin du niveau, et l'intervalle redevient une
 * distance qu'on mesure à l'œil. Le rail cesse d'être une frise de rythme
 * annotée pour devenir la partition du **niveau** : au niveau quatre la triade
 * de droite est fabriquée et la basse est publiée, et la portée grave
 * exactement ça, sans rien cacher et sans rien ajouter.
 *
 * ## Ce qui reste dans ce fichier, et ce qui n'y est plus
 *
 * Il ne reste ici que le squelette, l'incipit, et la phrase du lecteur d'écran.
 * Tout le dessin est parti dans deux modules, et le partage n'est pas de
 * confort :
 *
 * - `score-layout.ts` grave le niveau, une fois, en pixels. Pur, donc
 *   vérifiable sans navigateur : la place d'une note, la largeur d'une
 *   altération, la fenêtre d'un nom s'y testent ;
 * - `score-engine.ts` pose des nœuds là où la gravure le dit, recycle ce qui
 *   sort de la fenêtre, et n'écrit qu'une matrice par image.
 *
 * Le rail vivait auparavant **entièrement** dans React, et les deux coûts que
 * cela imposait étaient l'un et l'autre mesurables. À l'image, le ruban vivait
 * sous un `mask-image`, qui force son sous-arbre à être re-rastérisé quand son
 * contenu bouge : le navigateur repeignait toute la partition soixante fois par
 * seconde. Au temps, la tête se promouvait en état React, et ce rendu-là
 * reparcourait la frise entière avant de jeter ce qui n'était pas visible — une
 * pointe de calcul qui tombait exactement sur le temps, c'est-à-dire là où un
 * à-coup s'entend.
 *
 * ## Ce qui passe encore par React
 *
 * Trois choses, et elles ne bougent pas à l'image : la boîte et ses hauteurs,
 * qui viennent de la gravure et sont constantes pendant tout un niveau ;
 * l'incipit, qui change quand le morceau change ; et la phrase du lecteur
 * d'écran, que le moteur remonte au changement de nom parce que c'est du texte,
 * et que du texte se déclare dans le document, pas dans une couche.
 *
 * ## Elle n'a pas d'horloge, elle traduit celle du studio
 *
 * La position vient de `getEffectiveTick()`, la position que le roll dessine,
 * lue à chaque image sur le ticker qui le dessine. Il n'y a donc rien à
 * synchroniser : la partition ne suit pas le studio, elle *est* le studio,
 * écrit autrement — la même position, à la même image, au tick près.
 *
 * ## Elle est aussi une commande, et par le même chemin
 *
 * On la fait défiler à la molette et au doigt, et ce geste déplace la tête de
 * lecture du **studio**, pas la partition : le roll s'en va avec elle, et la
 * partition ne bouge que parce que le studio a bougé. Rien n'est réimplémenté,
 * ni l'inertie ni le `seekToTick` du relâchement. Voir `useStudioNavigation`.
 *
 * ## Elle ne sait rien du niveau qui l'emploie
 *
 * Ses entrées sont ce qu'elle grave, et rien d'autre : une frise, la durée d'un
 * temps, le chiffrage, le tempo. Ni porte, ni prise, ni accord courant, ni
 * unité. La même phrase vaut alors en travail comme en lecture.
 *
 * ## Rien n'est jugé
 *
 * La partition ne dit pas si l'on est en retard, parce qu'on ne peut pas
 * l'être : la frise s'arrête à la porte suivante et attend. Pas de couleur
 * d'erreur, pas de compte, pas de message. Voir `use-rhythm-take`.
 */
export function ScoreRail({
  frieze,
  beatTicks,
  meter,
  bpm,
}: {
  /**
   * Tout ce que le niveau donne à jouer, bout à bout, en ticks.
   *
   * Une propriété du **niveau** et non de l'état de jeu : elle ne se recalcule
   * pas quand le joueur avance, seule la tête de lecture bouge.
   */
  readonly frieze: Frieze;
  /** La durée d'un temps, en ticks : c'est elle qui donne le zoom. */
  readonly beatTicks: number;
  /** Le chiffrage, `null` quand le morceau n'en publie pas. */
  readonly meter: string | null;
  /** Le tempo **entendu**, celui du ralenti d'écoute, ou `null`. */
  readonly bpm: number | null;
}) {
  /*
    La gravure du niveau, faite une fois.

    Mémorisée sur la frise et le temps, qui sont les deux seules choses dont
    elle dépend : ni la tête de lecture, ni les touches tenues, ni les réglages
    d'écriture n'y entrent. C'est ce qui garantit qu'aucun rendu de ce
    composant ne peut coûter la longueur du morceau.
  */
  const layout = useMemo(() => layoutScore(frieze, beatTicks), [frieze, beatTicks]);

  /**
   * La surface du geste, et ce qu'un de ses pixels vaut en ticks.
   *
   * Le défilement de la partition déplace la tête de lecture du **studio** : le
   * roll s'en va avec elle, et la partition ne bouge que parce que le studio a
   * bougé.
   */
  const surface = useRef<HTMLDivElement | null>(null);
  const frameHost = useRef<HTMLDivElement | null>(null);
  const legendHost = useRef<HTMLDivElement | null>(null);
  const staffHost = useRef<HTMLDivElement | null>(null);
  const engine = useRef<ScoreEngine | null>(null);

  const [spoken, setSpoken] = useState("");
  const pressed = useActivePianoNotes();
  const noteNames = useNoteNamesOnScore();

  useStudioNavigation({ surfaceRef: surface, ticksPerPixel: layout.zoom > 0 ? 1 / layout.zoom : 0 });

  useLayoutEffect(() => {
    const frame = frameHost.current;
    const legend = legendHost.current;
    const staff = staffHost.current;

    if (frame === null || legend === null || staff === null) {
      return;
    }

    const built = new ScoreEngine(frame, legend, staff);

    built.onSpoken = setSpoken;
    engine.current = built;

    return () => {
      built.destroy();
      engine.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    engine.current?.setLayout(layout);
  }, [layout]);

  useLayoutEffect(() => {
    engine.current?.setNoteNames(noteNames);
  }, [noteNames]);

  useLayoutEffect(() => {
    engine.current?.setPressed(pressed);
  }, [pressed]);

  /*
    L'unique appel par image. Le rappel est stable de rendu en rendu — il ne se
    referme sur rien qui change — et le moteur décide seul de ce qu'il y a à
    faire, ce qui est presque toujours une affectation de `transform`.
  */
  useStudioTick((tick) => engine.current?.frame(tick));

  return (
    <div
      role="presentation"
      ref={surface}
      style={{
        width: "100%",
        height: layout.metrics.height,
        display: "flex",
        flexDirection: "column",
        gap: RAIL_GAP_PX,
        // Le navigateur ne doit pas prendre le glissé pour lui : sur tablette,
        // qui est la posture de référence du produit, il en ferait un
        // défilement de page ou un zoom au lieu d'un déplacement du morceau.
        touchAction: "none",
      }}
    >
      <Header meter={meter} bpm={bpm} legendWidth={layout.metrics.legendWidth} />

      <div style={{ display: "flex", flex: "1 1 auto", minWidth: 0, position: "relative" }}>
        {/*
          Les dix lignes, sous la clé comme sous les notes.

          Hors du cadre, dont le bord gauche s'efface : tracées dedans, elles
          commençaient après la marge et les clés flottaient dans le vide. Ici
          elles passent derrière les clés, comme sur une partition, et seules
          les notes s'effacent en arrivant sur la marge.
        */}
        <div
          aria-hidden
          ref={staffHost}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: layout.metrics.legendWidth,
            height: layout.metrics.viewHeight,
            pointerEvents: "none",
          }}
        />

        {/*
          La marge de gauche : la clé de chaque portée, puis l'armure. Elles
          tiennent la place des lettres `MD` et `MG`, et elles disent mieux ce
          que celles-ci disaient. Le moteur les grave, une fois par niveau.
        */}
        <div
          aria-hidden
          ref={legendHost}
          style={{
            width: layout.metrics.legendWidth,
            flex: "0 0 auto",
            position: "relative",
            height: layout.metrics.viewHeight,
          }}
        />

        {/* Le cadre : quatre couches, et le moteur seul y touche. */}
        <div ref={frameHost} style={{ flex: "1 1 auto", minWidth: 0, height: layout.metrics.viewHeight }} />

        {/*
          La marge de droite, vide et de la largeur de la légende : c'est elle
          qui garde la tête de lecture au milieu du rail, donc au milieu de
          l'écran, et non décalée d'une demi-légende vers la droite.
        */}
        <span style={{ width: layout.metrics.legendWidth, flex: "0 0 auto" }} />
      </div>

      <span className="sr-only">{spoken}</span>
    </div>
  );
}

/**
 * Ce que le passage dit de lui-même : son chiffrage et son tempo.
 *
 * Le tempo est celui qu'on **entend**, ralenti compris, et non celui qui est
 * écrit : c'est sur lui qu'on compte, et afficher l'autre ferait compter à
 * côté. Les deux disparaissent quand on ne les connaît pas, plutôt que de
 * laisser un cadre vide.
 *
 * C'est ce qu'une partition écrit à son incipit, à la place exacte où elle
 * l'écrit, et c'est aussi ce qui remplace le chiffrage gravé sur les portées :
 * un « 4/4 » posé sur deux portées se lirait deux fois pour une seule
 * information.
 */
function Header({
  meter,
  bpm,
  legendWidth,
}: {
  readonly meter: string | null;
  readonly bpm: number | null;
  /** La largeur de la tête de système, pour que le bandeau s'aligne dessus. */
  readonly legendWidth: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        paddingLeft: legendWidth,
        paddingRight: legendWidth,
        fontFamily: FONT_MONO,
        fontSize: HEADER_HEIGHT_PX,
        lineHeight: 1,
        letterSpacing: "0.18em",
        color: TEXT_MUTE,
        textTransform: "uppercase",
      }}
    >
      <span>Partition</span>

      <span style={{ letterSpacing: "0.08em" }}>
        {[meter, bpm === null ? null : `♩ = ${Math.round(bpm)}`].filter((part) => part !== null).join("  ·  ")}
      </span>
    </div>
  );
}
